/**
 * The one place this panel talks to the API.
 *
 * The panel is the platform operator's and nothing else (standing rule: no
 * tenant-facing UI), so there is one surface, one session and one root:
 * `/api/platform/v1`. Pages do not call this module — they call the typed
 * functions in `lib/platform`, which are the only callers of `api()` besides
 * `lib/upload.ts`.
 *
 * What it exists to hold:
 *
 * **Bearer only** (`.claude/skills/bearer-only`). The API sets no cookie and
 * reads none. Tokens come back in the sign-in body, are held in
 * `sessionStorage`, and are attached here as `Authorization: Bearer` — here
 * and nowhere else. No request sets `credentials`: the default (`same-origin`
 * for a same-origin call) would attach a cookie if one existed, and none does;
 * a header is attached only by the code that chose to attach it, which is the
 * whole argument against CSRF.
 *
 * **One refresh at a time.** A screen fires several requests on load. When the
 * access token expires they all 401 together, and a per-call refresh would send
 * several. `refreshSession` is single-flight, and a request that 401s with a
 * token that has *already* been replaced simply retries with the new one.
 *
 * **One Idempotency-Key per intent.** Minted once per `api()` call and reused
 * when that call is retried after a refresh, so the server sees one intent.
 *
 * **One envelope.** Every response is `{ success, code, reason, message, data,
 * errors?, traceId }` (`src/infra/http/envelope.ts`). The brief calls this
 * "RFC 9457 problem JSON" — it was, and is not any more; the filter in
 * `src/infra/http/api-exception.filter.ts` is the source of truth.
 */

import { t, type MessageKey } from './i18n';

/** Every platform path is relative to this. `/platform/v1` is not repeated at a call site. */
export const API_ROOT = '/api/platform/v1';

/** The refresh route, here because the refresh is this module's job. Equal to `ROUTES.refresh.path`. */
export const REFRESH_PATH = '/auth/refresh';

const WRITES = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

/**
 * Routes that carry no `Idempotency-Key`. Their request is a password or a
 * refresh token and their response is a token pair, and the API stores a
 * fingerprint of the one and a copy of the other against a key (security review
 * H-1). The server ignores a key on them now; not sending one means nothing is
 * minted for a credential to be filed under.
 */
const KEYLESS_PATHS = new Set(['/auth/login', REFRESH_PATH]);

/* ------------------------------------------------------------------ */
/* Tokens                                                               */
/* ------------------------------------------------------------------ */

export interface StoredTokens {
  accessToken: string;
  refreshToken: string;
}

/**
 * `sessionStorage`, not `localStorage`: it dies with the tab, so a shared or
 * forgotten machine does not carry a session into the next person's browser.
 * The key is the one the previous panel used, so a reload across the deploy
 * keeps the operator signed in.
 */
const TOKEN_KEY = 'ayed.platformSession';

function storage(): Storage | null {
  try {
    return globalThis.sessionStorage ?? null;
  } catch {
    // Storage disabled. No session can be held, which reads as signed out.
    return null;
  }
}

export const tokens = {
  read(): StoredTokens | null {
    const raw = storage()?.getItem(TOKEN_KEY) ?? null;

    if (raw === null) return null;

    try {
      const parsed = JSON.parse(raw) as Partial<StoredTokens>;

      return typeof parsed.accessToken === 'string' && typeof parsed.refreshToken === 'string'
        ? { accessToken: parsed.accessToken, refreshToken: parsed.refreshToken }
        : null;
    } catch {
      return null;
    }
  },

  /** Only the two tokens are stored — never whatever else a response carried. */
  write(value: StoredTokens): void {
    storage()?.setItem(TOKEN_KEY, JSON.stringify({ accessToken: value.accessToken, refreshToken: value.refreshToken }));
  },

  clear(): void {
    storage()?.removeItem(TOKEN_KEY);
  },

  present(): boolean {
    return this.read() !== null;
  },
};

function isTokens(value: unknown): value is StoredTokens {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as StoredTokens).accessToken === 'string' &&
    typeof (value as StoredTokens).refreshToken === 'string'
  );
}

/* ------------------------------------------------------------------ */
/* Session-expired notification                                         */
/* ------------------------------------------------------------------ */

type Listener = () => void;

const expiredListeners = new Set<Listener>();

/**
 * Called when the session is gone for good — the refresh was refused, or a
 * freshly refreshed token was refused too. `OperatorProvider` subscribes and
 * drops the operator, which sends the route guard to `/sign-in?next=…`.
 */
export function onSessionExpired(listener: Listener): () => void {
  expiredListeners.add(listener);

  return () => expiredListeners.delete(listener);
}

function sessionExpired(): void {
  tokens.clear();

  for (const listener of expiredListeners) listener();
}

/* ------------------------------------------------------------------ */
/* Errors                                                               */
/* ------------------------------------------------------------------ */

/**
 * The reasons the API names (`REASONS` in `src/infra/http/codes.ts`). Open:
 * a reason this panel has not heard of is still a string a page can compare,
 * and every screen falls back to the status when it does not recognise one.
 */
export type KnownReason =
  | 'token_expired'
  | 'session_revoked'
  | 'password_change_required'
  | 'no_change'
  | 'already_stored'
  | 'MODEL_UNKNOWN'
  | 'MODEL_IS_DEFAULT'
  | 'SLUG_TAKEN'
  | 'VERTICAL_UNKNOWN'
  | 'PERMISSION_OPENS_CONTENT'
  | 'OPERATOR_ADDRESS'
  | 'OPENING_PASSWORD_SUPERUSER_ONLY';

/**
 * The catalogue's sentence for a reason, used only when the API sent none of
 * its own (a proxy stripped the body, or an older server). The API's Arabic
 * `message` always wins: it is more specific, and the brief's rule is to
 * surface what the API says.
 */
const REASON_FALLBACK: Partial<Record<KnownReason, MessageKey>> = {
  SLUG_TAKEN: 'error.slugTaken',
  VERTICAL_UNKNOWN: 'error.verticalUnknown',
  MODEL_UNKNOWN: 'error.modelUnknown',
  MODEL_IS_DEFAULT: 'error.modelIsDefault',
  PERMISSION_OPENS_CONTENT: 'error.permissionOpensContent',
  OPERATOR_ADDRESS: 'error.operatorAddress',
  OPENING_PASSWORD_SUPERUSER_ONLY: 'error.openingPasswordSuperuserOnly',
};

export type ApiReason = KnownReason | (string & {});

export interface Envelope<T> {
  success: boolean;
  code: number;
  reason: ApiReason | null;
  message: string;
  data: T;
  errors?: unknown;
  traceId?: string;
}

export interface FieldError {
  /** The field in the API's vocabulary — the request body's key path, e.g. `ownerEmail` or `files.0.sha256`. */
  path: string;
  /** Already in Arabic: the panel sends `Accept-Language: ar`. */
  message: string;
}

/** The API refused. Everything a screen needs to say why, and where. */
export class ApiError extends Error {
  readonly status: number;
  readonly reason: ApiReason | null;
  /** The sentence the API sent, in Arabic. Null when the body was not ours (a proxy's HTML page). */
  readonly serverMessage: string | null;
  readonly fields: readonly FieldError[];
  readonly traceId: string | null;
  /** Seconds, from `Retry-After`, on a 429. */
  readonly retryAfter: number | null;

  constructor(
    status: number,
    body: Partial<Envelope<unknown>> | null = null,
    extra: { retryAfter?: number | null } = {},
  ) {
    const serverMessage = typeof body?.message === 'string' && body.message.length > 0 ? body.message : null;

    super(serverMessage ?? `HTTP ${status}`);
    this.name = 'ApiError';
    this.status = status;
    this.reason = typeof body?.reason === 'string' ? body.reason : null;
    this.serverMessage = serverMessage;
    this.fields = readFields(body?.errors);
    this.traceId = typeof body?.traceId === 'string' ? body.traceId : null;
    this.retryAfter = extra.retryAfter ?? null;
  }

  /**
   * The message for one form field, or undefined.
   *
   * Matches the path exactly or as a prefix (`allowedEmailDomains` matches
   * `allowedEmailDomains.2`), so a form can put a nested refusal on the input
   * it belongs to.
   */
  fieldError(path: string): string | undefined {
    return this.fields.find((field) => field.path === path || field.path.startsWith(`${path}.`))?.message;
  }

  /** Field errors as a map, first message per path. */
  get fieldErrors(): Readonly<Record<string, string>> {
    const map: Record<string, string> = {};

    for (const field of this.fields) map[field.path] ??= field.message;

    return map;
  }

  is(reason: KnownReason): boolean {
    return this.reason === reason;
  }

  get isUnauthorized(): boolean {
    return this.status === 401;
  }

  get isForbidden(): boolean {
    return this.status === 403;
  }

  get isNotFound(): boolean {
    return this.status === 404;
  }

  get isConflict(): boolean {
    return this.status === 409;
  }

  get isValidation(): boolean {
    return this.status === 400 || this.status === 422;
  }

  get isRateLimited(): boolean {
    return this.status === 429;
  }
}

/** The refresh failed too: there is no session. The shell redirects to sign-in. */
export class SessionExpiredError extends Error {
  constructor() {
    super(t('error.sessionExpired'));
    this.name = 'SessionExpiredError';
  }
}

/** `fetch` itself threw — offline, DNS, the proxy is down. Never an answer from the API. */
export class NetworkError extends Error {
  constructor(readonly cause: unknown) {
    super(t('error.unreachable'));
    this.name = 'NetworkError';
  }
}

/** An aborted request. Not an error to show: somebody navigated away, or a newer request replaced it. */
export function isAbort(error: unknown): boolean {
  return typeof error === 'object' && error !== null && (error as { name?: unknown }).name === 'AbortError';
}

/**
 * Read defensively: `errors` is whatever the server sent, and assuming its
 * shape would turn a malformed refusal into a crash while the user is already
 * being told something went wrong.
 */
function readFields(errors: unknown): FieldError[] {
  if (!Array.isArray(errors)) return [];

  return errors.flatMap((entry): FieldError[] => {
    if (typeof entry !== 'object' || entry === null) return [];

    const { path, message } = entry as { path?: unknown; message?: unknown };

    if (typeof message !== 'string' || message.length === 0) return [];

    return [{ path: typeof path === 'string' ? path : '', message }];
  });
}

/**
 * One readable Arabic sentence for any failure.
 *
 * Field errors first — the API sends one per rejected input, and the generic
 * 400 sentence ("the request failed validation") tells somebody who mistyped a
 * port nothing. Then the API's own sentence. Then a fallback by status, so a
 * proxy's HTML error page is never shown as text.
 */
export function messageOf(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.fields.length > 0) {
      return error.fields.map((field) => (field.path === '' ? field.message : `${field.path}: ${field.message}`)).join(' · ');
    }

    if (error.serverMessage !== null) return error.serverMessage;

    const fallback = error.reason === null ? undefined : REASON_FALLBACK[error.reason as KnownReason];

    if (fallback !== undefined) return t(fallback);

    if (error.status === 429) {
      return error.retryAfter === null
        ? t('error.rateLimitedShort')
        : t('error.rateLimited', { seconds: error.retryAfter });
    }

    if (error.status === 403) return t('error.forbidden');
    if (error.status === 404) return t('error.notFound');
    if (error.status >= 500) return t('error.server');

    return t('error.request', { status: error.status });
  }

  if (error instanceof SessionExpiredError || error instanceof NetworkError) return error.message;

  // `fetch` rejects with a TypeError when the network fails outright.
  if (error instanceof TypeError) return t('error.unreachable');

  return error instanceof Error && error.message.length > 0 ? error.message : t('error.unexpected');
}

/* ------------------------------------------------------------------ */
/* Requests                                                             */
/* ------------------------------------------------------------------ */

export type Query = Record<string, string | number | boolean | null | undefined>;

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  query?: Query;
  signal?: AbortSignal;
  /**
   * Reuse a key so the API recognises a retry as the same intent. Left out, one
   * is minted per call — correct for a button somebody pressed.
   */
  idempotencyKey?: string;
  /**
   * False for sign-in and refresh: no bearer is sent and a 401 is an answer to
   * show ("wrong email or password"), not a reason to refresh.
   */
  auth?: boolean;
}

/** A success, with what the envelope says besides `data` — `reason: 'no_change'` is the one screens read. */
export interface ApiResult<T> {
  data: T;
  status: number;
  reason: ApiReason | null;
  message: string | null;
  traceId: string | null;
}

/**
 * `crypto.randomUUID` where it exists — every browser this panel supports over
 * https. The fallback is for a development host on plain http, where it is
 * undefined and every write would otherwise fail with a 400 unrelated to the
 * code being written. Either form satisfies the server's 8–200 printable ASCII.
 */
export function newIdempotencyKey(): string {
  if (typeof globalThis.crypto?.randomUUID === 'function') return globalThis.crypto.randomUUID();

  return `k-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

/**
 * A path that already carries the root, or another version, is a call-site
 * bug — it would compile, build, and 404. Refused loudly instead, on the first
 * request, in development and in tests alike.
 */
function urlFor(path: string, query: Query | undefined): string {
  if (!path.startsWith('/') || path.startsWith('/api/') || path.startsWith('/platform/') || /\/v\d+(\/|$)/.test(path)) {
    throw new Error(`api(): "${path}" must be relative to ${API_ROOT} — pass "/tenants", not "${API_ROOT}/tenants".`);
  }

  const search = new URLSearchParams();

  for (const [key, value] of Object.entries(query ?? {})) {
    if (value !== undefined && value !== null && value !== '') search.set(key, String(value));
  }

  const qs = search.toString();

  return `${API_ROOT}${path}${qs === '' ? '' : `?${qs}`}`;
}

interface Sent {
  response: Response;
  /** The access token this request carried, to tell "expired" from "already replaced". */
  bearer: string | null;
}

async function send(path: string, options: RequestOptions, key: string | undefined): Promise<Sent> {
  // Outside the `try` below: a malformed path is a programming error, and
  // reporting it as "the server is unreachable" would send somebody to check
  // the network instead of the call site.
  const url = urlFor(path, options.query);
  const method = options.method ?? 'GET';
  const headers: Record<string, string> = {
    Accept: 'application/json',
    // The panel is Arabic, so it asks for Arabic; the API translates `message`
    // and every field error into it.
    'Accept-Language': 'ar',
  };

  if (key !== undefined) headers['Idempotency-Key'] = key;

  const bearer = options.auth === false ? null : (tokens.read()?.accessToken ?? null);

  if (bearer !== null) headers.Authorization = `Bearer ${bearer}`;

  const init: RequestInit = { method, headers };

  if (options.body !== undefined) {
    headers['Content-Type'] = 'application/json';
    init.body = JSON.stringify(options.body);
  }

  if (options.signal !== undefined) init.signal = options.signal;

  try {
    return { response: await fetch(url, init), bearer };
  } catch (failure) {
    if (isAbort(failure)) throw failure;

    throw new NetworkError(failure);
  }
}

function isEnvelope(value: unknown): value is Envelope<unknown> {
  return typeof value === 'object' && value !== null && 'success' in value && 'code' in value;
}

function retryAfterOf(response: Response): number | null {
  const raw = response.headers.get('Retry-After');
  const seconds = raw === null ? Number.NaN : Number(raw);

  return Number.isFinite(seconds) && seconds > 0 ? Math.ceil(seconds) : null;
}

async function read<T>(response: Response): Promise<ApiResult<T>> {
  const text = response.status === 204 ? '' : await response.text();
  let payload: unknown;

  try {
    payload = text.length > 0 ? JSON.parse(text) : undefined;
  } catch {
    // Not JSON — an nginx page or a gateway timeout. Its text is not shown:
    // an HTML document is not a sentence.
    payload = undefined;
  }

  if (!response.ok) {
    throw new ApiError(response.status, isEnvelope(payload) ? payload : null, { retryAfter: retryAfterOf(response) });
  }

  if (isEnvelope(payload)) {
    return {
      data: payload.data as T,
      status: response.status,
      reason: payload.reason ?? null,
      message: typeof payload.message === 'string' ? payload.message : null,
      traceId: typeof payload.traceId === 'string' ? payload.traceId : null,
    };
  }

  return { data: payload as T, status: response.status, reason: null, message: null, traceId: null };
}

/** The full answer, envelope and all. Use when `reason` matters. */
export async function apiResult<T>(path: string, options: RequestOptions = {}): Promise<ApiResult<T>> {
  const method = options.method ?? 'GET';
  // Minted once, so the retry below is the same intent to the server.
  const key =
    WRITES.has(method) && !KEYLESS_PATHS.has(path) ? (options.idempotencyKey ?? newIdempotencyKey()) : undefined;

  const first = await send(path, options, key);

  if (first.response.status !== 401 || options.auth === false) return read<T>(first.response);

  // The platform guard answers every 401 alike (no `token_expired` reason on
  // this surface), so any 401 on an authenticated call is worth one refresh.
  const current = tokens.read()?.accessToken ?? null;
  const replaced = current !== null && current !== first.bearer;

  if (!replaced && !(await refreshSession())) {
    sessionExpired();
    throw new SessionExpiredError();
  }

  const second = await send(path, options, key);

  if (second.response.status === 401) {
    // A token issued a moment ago and refused anyway: revoked, suspended, or
    // signed out elsewhere. Refreshing again would only loop.
    sessionExpired();
    throw new SessionExpiredError();
  }

  return read<T>(second.response);
}

/** `data` alone — what nearly every caller wants. */
export async function api<T>(path: string, options: RequestOptions = {}): Promise<T> {
  return (await apiResult<T>(path, options)).data;
}

/* ------------------------------------------------------------------ */
/* Refresh                                                              */
/* ------------------------------------------------------------------ */

let inFlight: Promise<boolean> | null = null;

/**
 * Trade the refresh token for a new pair. Single-flight: every caller that
 * arrives while one is running awaits the same promise.
 *
 * The answer is an envelope like every other — the tokens are in `data`. The
 * previous client stored the whole envelope as the session, which then had no
 * `accessToken`, so every refresh "succeeded" and signed the operator out.
 *
 * A refused refresh clears the stored pair (a token the server has refused is
 * one no later call should present). A network failure does not: the session
 * may be perfectly good and the Wi-Fi is what failed.
 */
export function refreshSession(): Promise<boolean> {
  if (inFlight !== null) return inFlight;

  const attempt = (async (): Promise<boolean> => {
    const held = tokens.read();

    if (held === null) return false;

    let sent: Sent;

    try {
      sent = await send(REFRESH_PATH, { method: 'POST', body: { refreshToken: held.refreshToken }, auth: false }, undefined);
    } catch {
      return false;
    }

    if (!sent.response.ok) {
      tokens.clear();

      return false;
    }

    try {
      const { data } = await read<unknown>(sent.response);

      if (!isTokens(data)) {
        tokens.clear();

        return false;
      }

      tokens.write(data);

      return true;
    } catch {
      return false;
    }
  })();

  inFlight = attempt;

  // Cleared from a `.finally` chained *outside* the async body. Inside it, a
  // body that returns before its first `await` (no stored session) runs its
  // `finally` before `inFlight` is even assigned — leaving a settled `false`
  // in the slot forever, and every later refresh answering `false` unasked.
  void attempt.finally(() => {
    if (inFlight === attempt) inFlight = null;
  });

  return attempt;
}
